import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarCheck, CalendarX, Clock, Link2Off } from "lucide-react";
import api from "@/lib/api";

const msg = (e) => e?.response?.data?.detail?.message || "Something went wrong. Please try again.";
const when = (b) => `${b.local_date} at ${b.local_time} ${b.tz_abbrev}`;
const bookLink = <Link to="/demo" className="pf-btn-primary px-5 py-2.5 rounded-full text-sm" data-testid="demo-cancel-rebook">Book a new time</Link>;

function Panel({ icon: Icon, title, children, testid }) {
  return (
    <div className="pf-card p-6 sm:p-8 text-center" data-testid={testid} role="status">
      <Icon className="w-10 h-10 text-cyan-600 mx-auto" />
      <h1 className="font-display text-2xl text-slate-900 mt-3" data-testid="demo-cancel-title">{title}</h1>
      {children}
    </div>
  );
}

export default function DemoCancel() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (token.length < 10) { setError("This cancel link isn't valid."); return; }
    api.post("/demo/cancel/lookup", { token }).then((r) => setData(r.data)).catch((e) => setError(msg(e)));
  }, [token]);

  const cancel = async () => {
    setBusy(true);
    try { const r = await api.post("/demo/cancel", { token }); setData(r.data); setDone(true); }
    catch (e) {
      const st = e?.response?.data?.detail?.state;
      if (st) setData((d) => ({ ...d, state: st })); else setError(msg(e));
    } finally { setBusy(false); }
  };

  let body = <p className="text-sm text-slate-500 text-center" data-testid="demo-cancel-loading">Loading your demo…</p>;
  if (error) body = (
    <Panel icon={Link2Off} title="This link doesn't work" testid="demo-cancel-invalid">
      <p className="text-sm text-slate-600 mt-2" data-testid="demo-cancel-error">{error} If you need help, just reply to your booking email.</p>
      <div className="mt-6 flex justify-center">{bookLink}</div>
    </Panel>);
  else if (data?.state === "ACTIVE") body = (
    <Panel icon={CalendarX} title="Cancel your Cyannect demo?" testid="demo-cancel-confirm">
      <p className="text-sm text-slate-600 mt-2">Your demo is booked for <strong data-testid="demo-cancel-when">{when(data.booking)}</strong> (Eastern Time, 20 minutes).</p>
      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <button type="button" onClick={cancel} disabled={busy} data-testid="demo-cancel-submit"
          className="px-5 py-2.5 rounded-full text-sm bg-rose-600 text-white hover:bg-rose-700 transition-colors disabled:opacity-50">
          {busy ? "Cancelling…" : "Cancel my demo"}</button>
        <Link to="/" className="px-5 py-2.5 rounded-full text-sm border border-slate-300 hover:border-slate-900 transition-colors" data-testid="demo-cancel-keep">Keep my demo</Link>
      </div>
    </Panel>);
  else if (data?.state === "CANCELLED") body = (
    <Panel icon={CalendarCheck} title={done ? "Your demo is cancelled" : "This demo is already cancelled"} testid="demo-cancel-done">
      <p className="text-sm text-slate-600 mt-2" data-testid="demo-cancel-done-text">
        {done ? <>Your demo on <strong>{when(data.booking)}</strong> has been cancelled. We've emailed you a confirmation.</>
          : <>The demo on <strong>{when(data.booking)}</strong> was already cancelled.</>}</p>
      <div className="mt-6 flex justify-center">{bookLink}</div>
    </Panel>);
  else if (data) body = (
    <Panel icon={Clock} title="This demo can't be cancelled here" testid="demo-cancel-closed">
      <p className="text-sm text-slate-600 mt-2">The demo on <strong>{when(data.booking)}</strong> has already started or finished. If you need help, just reply to your booking email.</p>
    </Panel>);

  return (
    <div className="max-w-xl mx-auto px-6 py-16" data-testid="demo-cancel-page">
      <Helmet><title>Cancel your Cyannect demo</title><meta name="robots" content="noindex" /></Helmet>
      {body}
    </div>
  );
}
